const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export function extractPlaceholders(body: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  let match: RegExpExecArray | null;
  PLACEHOLDER_PATTERN.lastIndex = 0;
  while ((match = PLACEHOLDER_PATTERN.exec(body)) !== null) {
    if (!seen.has(match[1])) {
      seen.add(match[1]);
      result.push(match[1]);
    }
  }
  return result;
}

export const PLACEHOLDER_DEFAULTS: Record<string, string> = {
  visual_style: "Premium editorial technology illustration with clean geometry, flat layered visuals, realistic lighting, strong contrast, controlled use of brand colors, minimal details, and one clear visual concept. The design should feel modern, polished, business-focused, and suitable for a senior technology professional. Avoid 3D objects, cartoon styling, excessive futuristic effects, stock-photo poses, random floating icons, and visual clutter.",
};

export function placeholderLabel(token: string): string {
  return token
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function fillPlaceholders(body: string, values: Record<string, string>, forceEmptyTokens?: Set<string>): string {
  return body.replace(PLACEHOLDER_PATTERN, (full, token) => {
    const value = values[token]?.trim();
    if (value) return value;
    return forceEmptyTokens?.has(token) ? "" : full;
  });
}

export interface ColorScheme {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  backgroundColor?: string | null;
}

const PREDEFINED_PLACEHOLDERS = new Set(["color_scheme", "color_scheme_inline"]);

export function isPredefinedPlaceholder(token: string): boolean {
  return PREDEFINED_PLACEHOLDERS.has(token);
}

export function formatColorScheme(scheme?: ColorScheme | null, separator: string = "\n"): string {
  if (!scheme) return "";
  const parts: string[] = [];
  if (scheme.primaryColor) parts.push(`Primary Color: ${scheme.primaryColor}`);
  if (scheme.secondaryColor) parts.push(`Secondary Color: ${scheme.secondaryColor}`);
  if (scheme.accentColor) parts.push(`Accent Color: ${scheme.accentColor}`);
  if (scheme.backgroundColor) parts.push(`Background Color: ${scheme.backgroundColor}`);
  return parts.join(separator);
}

export function resolvePredefinedValues(tokens: string[], ctx: { colorScheme?: ColorScheme | null }): Record<string, string> {
  const result: Record<string, string> = {};
  for (const token of tokens) {
    if (token === "color_scheme") {
      result[token] = formatColorScheme(ctx.colorScheme, "\n");
    } else if (token === "color_scheme_inline") {
      result[token] = formatColorScheme(ctx.colorScheme, ", ");
    }
  }
  return result;
}

export interface PromptSegment {
  type: "text" | "placeholder";
  text: string;
  token?: string;
}

export function splitIntoSegments(body: string): PromptSegment[] {
  const segments: PromptSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  PLACEHOLDER_PATTERN.lastIndex = 0;
  while ((match = PLACEHOLDER_PATTERN.exec(body)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", text: body.slice(lastIndex, match.index) });
    }
    segments.push({ type: "placeholder", text: match[0], token: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ type: "text", text: body.slice(lastIndex) });
  }
  return segments;
}

export interface PlaceholderColor {
  text: string;
  bg: string;
  border: string;
  focusBorder: string;
}

// Full literal Tailwind class names (not template-built) so the JIT scanner picks them up.
export const PLACEHOLDER_COLORS: PlaceholderColor[] = [
  { text: "text-amber-300", bg: "bg-amber-500/20", border: "border-amber-500/30", focusBorder: "focus:border-amber-500" },
  { text: "text-sky-300", bg: "bg-sky-500/20", border: "border-sky-500/30", focusBorder: "focus:border-sky-500" },
  { text: "text-emerald-300", bg: "bg-emerald-500/20", border: "border-emerald-500/30", focusBorder: "focus:border-emerald-500" },
  { text: "text-fuchsia-300", bg: "bg-fuchsia-500/20", border: "border-fuchsia-500/30", focusBorder: "focus:border-fuchsia-500" },
  { text: "text-rose-300", bg: "bg-rose-500/20", border: "border-rose-500/30", focusBorder: "focus:border-rose-500" },
  { text: "text-lime-300", bg: "bg-lime-500/20", border: "border-lime-500/30", focusBorder: "focus:border-lime-500" },
  { text: "text-cyan-300", bg: "bg-cyan-500/20", border: "border-cyan-500/30", focusBorder: "focus:border-cyan-500" },
  { text: "text-orange-300", bg: "bg-orange-500/20", border: "border-orange-500/30", focusBorder: "focus:border-orange-500" },
];

export function colorForPlaceholder(token: string, allTokens: string[]): PlaceholderColor {
  const idx = allTokens.indexOf(token);
  return PLACEHOLDER_COLORS[(idx === -1 ? 0 : idx) % PLACEHOLDER_COLORS.length];
}

export interface SystemPlaceholderInfo {
  token: string;
  kind: "auto" | "default";
  description: string;
}

// Reference catalog for prompt authors — not used in substitution logic itself.
export const SYSTEM_PLACEHOLDERS: SystemPlaceholderInfo[] = [
  { token: "color_scheme", kind: "auto", description: "Auto-filled from the user's profile colors, one per line (Primary/Secondary/Accent/Background). No input shown; omitted entirely if no colors are set." },
  { token: "color_scheme_inline", kind: "auto", description: "Same colors as color_scheme, but comma-separated on one line — for use inline within a sentence." },
  { token: "visual_style", kind: "default", description: "Pre-filled with a default illustration-style description. Still editable per use via an Edit link." },
];
